import {Choice, Label, NumberInput, Segment, Switch} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {isNil, isNotEmpty, isNotNil, trim} from 'ramda';

import type {TestIdProps} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {NumericBoundBody} from '../../../../../types/api/api';
import {
  getNumericBoundMagnitudeInvalidEnds,
  mergeNumericBoundFieldError,
  type NumericBoundFieldErrorState,
} from '../utils/mergeNumericBoundFieldError';

interface UnitLabels {
  abs: string;
  pct: string;
}

interface BoundaryRowProps extends TestIdProps {
  label: string;
  /** null = disabled (all bounds off) */
  value: NumericBoundBody | null;
  onChange: (next: NumericBoundBody | null) => void;
  /** When present, shows an abs/pct segment toggle. The parent owns which side is active. */
  unitLabels?: UnitLabels;
  unit?: 'abs' | 'pct';
  onUnitChange?: (u: 'abs' | 'pct') => void;
  isReadOnly?: boolean;
  /** Pass `fieldState.error` from react-hook-form; visible text is derived with `mergeNumericBoundFieldError`. */
  errorMessage?: NumericBoundFieldErrorState;
}

type EndMode = 'any' | 'same' | 'offset';

/** Derive UI dropdown state from a signed offset stored on the BE. */
function endModeFromValue(v: number | null): EndMode {
  if (isNil(v)) {
    return 'any';
  }
  if (v === 0) {
    return 'same';
  }
  return 'offset';
}

/** Convert dropdown state + magnitude to a signed offset. */
function valueFromEndMode(
  mode: EndMode,
  magnitude: number,
  direction: 'from' | 'to'
): number | null {
  if (mode === 'any') {
    return null;
  }
  if (mode === 'same') {
    return 0;
  }
  return direction === 'from' ? -Math.abs(magnitude) : Math.abs(magnitude);
}

function EndSelector({
  signedValue,
  onChange,
  direction,
  unitLabel,
  isReadOnly,
  isInvalid,
}: {
  signedValue: number | null;
  onChange: (next: number | null) => void;
  direction: 'from' | 'to';
  unitLabel: string;
  isReadOnly?: boolean;
  isInvalid?: boolean;
}) {
  const mode = endModeFromValue(signedValue);
  const magnitude = isNotNil(signedValue) ? Math.abs(signedValue) : 0;

  const labelKey =
    direction === 'from'
      ? 'entity.filterForm.savedFilters.bound.from'
      : 'entity.filterForm.savedFilters.bound.to';

  const offsetTranslationKey =
    direction === 'from'
      ? 'entity.filterForm.savedFilters.bound.offsetMinus'
      : 'entity.filterForm.savedFilters.bound.offsetPlus';

  const offsetLabel = unitLabel
    ? `${i18n.t(offsetTranslationKey)} ${unitLabel}`
    : i18n.t(offsetTranslationKey);

  const options: {value: EndMode; label: string}[] = [
    {value: 'any', label: i18n.t('entity.filterForm.savedFilters.bound.any')},
    {value: 'same', label: i18n.t('entity.filterForm.savedFilters.bound.same')},
    {value: 'offset', label: offsetLabel},
  ];

  const handleModeChange = (next: EndMode | null) => {
    if (isNil(next)) {
      return;
    }
    // When switching to offset, ensure a non-zero magnitude so the value isn't
    // immediately interpreted as "same as vehicle" (signed 0).
    const effectiveMagnitude = next === 'offset' ? Math.max(magnitude, 1) : magnitude;
    onChange(valueFromEndMode(next, effectiveMagnitude, direction));
  };

  const handleMagnitudeChange = (val: number | null) => {
    onChange(valueFromEndMode('offset', val ?? 0, direction));
  };

  const isOffset = mode === 'offset';

  return (
    <>
      <Text size="small" color="secondary">
        {i18n.t(labelKey)}
      </Text>
      <Choice<EndMode>
        value={mode}
        options={options}
        onChange={handleModeChange}
        isDisabled={isReadOnly}
        isNotClearable
      />
      <Box>
        <Show when={isOffset}>
          <NumberInput
            value={magnitude}
            onChange={handleMagnitudeChange}
            isDisabled={isReadOnly}
            decimalPlaces={0}
            isInvalid={!!isInvalid}
          />
        </Show>
      </Box>
      <Box>
        <Show when={isOffset}>
          <Text size="small" color="secondary">
            {unitLabel}
          </Text>
        </Show>
      </Box>
    </>
  );
}

export function BoundaryRow(props: BoundaryRowProps) {
  const {
    label,
    value,
    onChange,
    unitLabels,
    unit = 'abs',
    onUnitChange,
    isReadOnly,
    errorMessage: fieldError,
  } = props;
  const isEnabled = isNotNil(value);
  const unitLabel = unit === 'pct' ? '%' : (unitLabels?.abs ?? '');

  const {isFromMagnitudeInvalid, isToMagnitudeInvalid} =
    getNumericBoundMagnitudeInvalidEnds(fieldError);
  const mergedErrorText = mergeNumericBoundFieldError(fieldError);
  const rowErrorLines = mergedErrorText?.split('\n').filter((line) => isNotEmpty(trim(line))) ?? [];
  const hasRowErrorMessage = rowErrorLines.length > 0;

  const handleToggle = (enabled: boolean) => {
    onChange(enabled ? {from: null, to: null} : null);
  };

  const updateFrom = (from: number | null) => {
    onChange({...(value ?? {from: null, to: null}), from});
  };

  const updateTo = (to: number | null) => {
    onChange({...(value ?? {from: null, to: null}), to});
  };

  const segmentOptions = unitLabels
    ? [
        {value: 'abs' as const, label: unitLabels.abs},
        {value: 'pct' as const, label: '%'},
      ]
    : undefined;

  return (
    <VStack spacing={2} data-testid={props['data-testid']}>
      <HStack align="center" justify="space-between" spacing={2}>
        <HStack align="center" spacing={2}>
          <Switch value={isEnabled} onChange={handleToggle} isDisabled={isReadOnly} />
          <Label>{label}</Label>
        </HStack>
        <Show when={isEnabled && !!unitLabels && !!onUnitChange && !!segmentOptions}>
          <Segment<'abs' | 'pct'>
            value={unit}
            options={segmentOptions}
            onChange={(u) => onUnitChange?.(u)}
            isDisabled={isReadOnly}
          />
        </Show>
      </HStack>
      <Show when={isEnabled}>
        <BoundaryGrid>
          <EndSelector
            signedValue={value?.from ?? null}
            onChange={updateFrom}
            direction="from"
            unitLabel={unitLabel}
            isReadOnly={isReadOnly}
            isInvalid={isFromMagnitudeInvalid}
          />
          <EndSelector
            signedValue={value?.to ?? null}
            onChange={updateTo}
            direction="to"
            unitLabel={unitLabel}
            isReadOnly={isReadOnly}
            isInvalid={isToMagnitudeInvalid}
          />
        </BoundaryGrid>
      </Show>
      <Show when={hasRowErrorMessage}>
        <VStack spacing={1}>
          {rowErrorLines.map((line) => (
            <Text key={line} size="xSmall" color="danger">
              {line}
            </Text>
          ))}
        </VStack>
      </Show>
    </VStack>
  );
}

// Platform <Grid /> only supports equal-width columns (repeat(N, 1fr)). This row needs
// four tracks with different sizing rules (fixed label, flexible dropdown, capped input,
// content-sized unit), so a local styled grid is used instead.
// Track layout: [From/To label] [mode dropdown] [magnitude input] [unit text]
// eslint-disable-next-line eag/no-css-property
const BoundaryGrid = styled.div`
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) minmax(0, 96px) auto;
  align-items: center;
  column-gap: 8px;
  row-gap: 8px;
`;
